import React, { useMemo } from 'react'
import { Fulfiller } from '../../../../types/Fulfiller'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useUpdateData } from '../../../../hooks/useUpdateData'
import { ApiRoutes } from '../../../../services/ApiService'
import { showToast } from '../../../../services/ToastService'
import { closeModal, openModal } from '../../../../services/ModalService'
import Input from '../../../../components/Input'
import cx from 'classnames'
import styles from './EditFulfillerModal.module.scss'
import Button from '../../../../components/Button'
import DeleteFulfillmentModal from '../DeleteFulfillmentModal/DeleteFulfillmentModal'

type EditFulfillerModalProps = {
    fulfiller: Fulfiller
    onSubmit: () => void
}

const EditFulfillerModal: React.FC<EditFulfillerModalProps> = ({
    fulfiller,
    onSubmit,
}) => {
    const { t } = useTranslation(['settings'])

    const schema = useMemo(() => {
        return {
            email: yup.string().required().min(3),
            password: yup.string().required().min(3),
        }
    }, [])

    const { element, changeProp, isValid, submit } = useUpdateData<Fulfiller>(
        ApiRoutes.FULFILLER,
        fulfiller,
        fulfiller.id,
        schema
    )

    const handleSubmit = () => {
        submit()
            .then((res) => {
                if (res.status === 200) {
                    showToast(t('fulfillment.edit_modal.success'))
                    onSubmit()
                    closeModal()
                } else {
                    showToast(t('fulfillment.edit_modal.success'), 'error')
                }
            })
            .catch(() => {
                showToast(t('fulfillment.edit_modal.success'))
            })
    }

    const openDeleteModal = () => {
        const content: JSX.Element = (
            <div style={{ minWidth: '350px' }}>
                <DeleteFulfillmentModal
                    fulfiller={fulfiller}
                    onSubmit={() => {
                        onSubmit()
                        closeModal()
                    }}
                />
            </div>
        )
        openModal({
            heading: t('fulfillment.delete_modal.heading'),
            content: content,
        })
    }

    return (
        <div className={cx(styles['modal-wrapper'])}>
            <Input
                property={'email'}
                type="text"
                placeholder={t('fulfillment.labels.email')}
                value={element.email}
                onChange={(_, value) => changeProp({ email: value })}
                classes={'zr-w-100 zr-mx-4 zr-mt-3 zr-mb-2'}
            />
            <Input
                property={'password'}
                type="password"
                placeholder={t('fulfillment.labels.password')}
                value={element.password}
                onChange={(_, value) => changeProp({ password: value })}
                classes={'zr-w-100 zr-mx-4 zr-my-3'}
            />
            <Button
                hierarchy="primary"
                text={t('fulfillment.edit_modal.action')}
                fn={() => handleSubmit()}
                disabled={!isValid}
                classes={'zr-w-100 zr-mx-4 zr-my-2'}
            />
            <Button
                hierarchy="secondary"
                text={t('fulfillment.edit_modal.delete_action')}
                fn={() => {
                    closeModal()
                    openDeleteModal()
                }}
                classes={'zr-w-100 zr-mx-4 zr-my-2'}
            />
        </div>
    )
}

export default EditFulfillerModal
